import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, message } from 'antd';
import { Input, Select } from 'formik-antd';
import { useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Box, FormInput, CustomInput } from 'atoms';
import { getPincodeList } from 'store/pageSlice';

const { Option } = Select;

const StyledInput = styled(Input)`
	border-radius: 4px;
	font-size: 14px;
	padding: 8px 12px;

	&::placeholder {
		color: #bfbfbf;
		font-size: 13px;
	}
`;

const DisabledInput = styled(CustomInput)`
	background-color: #dfdfdf !important;
	color: #1c1c1c !important;
	cursor: not-allowed;

	input {
		background-color: #dfdfdf !important;
		color: #1c1c1c !important;
		cursor: not-allowed;
	}
`;

export default function BasicInformationStep({ isEditable = true, isDisabled = false }) {
	const dispatch = useDispatch();
	const { setFieldValue } = useFormikContext();
	const disabled = !isEditable || isDisabled;

	/**
	 * Clear location fields (zone, city, state)
	 */
	const clearLocationFields = () => {
		setFieldValue('zone', '');
		setFieldValue('city', '');
		setFieldValue('state', '');
	};

	/**
	 * Populate location fields from pincode data
	 */
	const populateLocationFields = (pincodeData) => {
		if (!pincodeData || typeof pincodeData !== 'object') {
			clearLocationFields();
			return;
		}

		const { zone, city, state } = pincodeData;
		if (zone) setFieldValue('zone', zone);
		if (city) setFieldValue('city', city);
		if (state) setFieldValue('state', state);
	};

	/**
	 * Validate and extract pincode data from API response
	 */
	const extractPincodeData = (response) => {
		if (
			!response ||
			!response.rows ||
			!Array.isArray(response.rows) ||
			response.rows.length === 0
		) {
			return null;
		}

		return response.rows[0];
	};

	/**
	 * Fetch pincode details from API
	 */
	const fetchPincodeDetails = async (pincode) => {
		try {
			const response = await dispatch(getPincodeList(pincode));
			const pincodeData = extractPincodeData(response);
			populateLocationFields(pincodeData);
		} catch (error) {
			console.error('Error fetching pincode details:', error);
			message.error('Failed to fetch pincode details. Please try again.');
			clearLocationFields();
		}
	};

	/**
	 * Handle pincode change - Auto-fetch zone, city, and state when pincode is 6 digits
	 */
	const handlePincodeChange = async (pincode) => {
		if (!pincode || typeof pincode !== 'string') {
			return;
		}

		const trimmedPincode = pincode.trim();

		if (trimmedPincode.length === 6) {
			await fetchPincodeDetails(trimmedPincode);
		} else if (trimmedPincode.length < 6) {
			clearLocationFields();
		}
	};

	return (
		<Box>
			<Row gutter={24}>
				<Col span={12}>
					<FormInput label="Vendor Type*" name="vendor_type">
						<Select name="vendor_type" placeholder="Select" disabled={disabled}>
							<Option value="regular">Regular</Option>
							<Option value="temporary">Temporary</Option>
						</Select>
					</FormInput>
				</Col>
				<Col span={12}>
					<FormInput label="Vendor Name*" name="vendor_name">
						<StyledInput
							name="vendor_name"
							placeholder="Eg : Flipkart"
							disabled={disabled}
						/>
					</FormInput>
				</Col>
			</Row>

			<Row gutter={24}>
				<Col span={12}>
					<FormInput label="Legal Structure*" name="legal_structure">
						<Select name="legal_structure" placeholder="Select" disabled={disabled}>
							<Option value="Company">Company</Option>
							<Option value="LLP">LLP</Option>
							<Option value="Individual">Individual</Option>
							<Option value="Aggregator">Aggregator</Option>
						</Select>
					</FormInput>
				</Col>
				<Col span={12}>
					<FormInput label="Vendor Email ID*" name="vendor_email_id">
						<StyledInput
							name="vendor_email_id"
							type="email"
							placeholder="Eg : Flipkart@gmail.com"
							disabled={disabled}
						/>
					</FormInput>
				</Col>
			</Row>

			<Row gutter={24}>
				<Col span={12}>
					<FormInput label="Primary PoC Name*" name="primary_poc_name">
						<StyledInput
							name="primary_poc_name"
							placeholder="Eg : Intelligent Retail"
							disabled={disabled}
						/>
					</FormInput>
				</Col>
				<Col span={12}>
					<FormInput label="Primary PoC Contact*" name="primary_poc_contact">
						<StyledInput
							name="primary_poc_contact"
							placeholder="Eg : 9876543076"
							disabled={disabled}
						/>
					</FormInput>
				</Col>
			</Row>

			<Row gutter={24}>
				<Col span={12}>
					<FormInput label="Alternative PoC Name" name="alternative_poc_name">
						<StyledInput
							name="alternative_poc_name"
							placeholder="Eg : Intelligent Retail"
							disabled={disabled}
						/>
					</FormInput>
				</Col>
				<Col span={12}>
					<FormInput label="Alternative PoC Contact" name="alternative_poc_contact">
						<StyledInput
							name="alternative_poc_contact"
							placeholder="Eg : 9876543076"
							disabled={disabled}
						/>
					</FormInput>
				</Col>
			</Row>

			<Row gutter={24}>
				<Col span={24}>
					<FormInput label="Vendor Address*" name="vendor_address">
						<StyledInput
							name="vendor_address"
							placeholder="Eg : Muneshwara layout, Bangalore"
							disabled={disabled}
						/>
					</FormInput>
				</Col>
			</Row>

			<Row gutter={24}>
				<Col span={6}>
					<FormInput label="Pincode*" name="pincode">
						<StyledInput
							name="pincode"
							placeholder="Eg : 517408"
							disabled={disabled}
							onChange={(e) => {
								if (!disabled) {
									const { value } = e.target;
									setFieldValue('pincode', value);
									handlePincodeChange(value);
								}
							}}
							maxLength={6}
						/>
					</FormInput>
				</Col>
				<Col span={6}>
					<FormInput label="Zone*" name="zone">
						<DisabledInput name="zone" placeholder="Enter Zone" disabled />
					</FormInput>
				</Col>
				<Col span={6}>
					<FormInput label="City" name="city">
						<DisabledInput name="city" placeholder="Enter City" disabled />
					</FormInput>
				</Col>
				<Col span={6}>
					<FormInput label="State" name="state">
						<DisabledInput name="state" placeholder="Enter City" disabled />
					</FormInput>
				</Col>
			</Row>
		</Box>
	);
}

BasicInformationStep.propTypes = {
	isEditable: PropTypes.bool,
	isDisabled: PropTypes.bool,
};
